import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { noWhiteSpaceValidator, PhysicianAccountPgpsDTO, ProviderAccountPgpDTO, ProviderAccountPgpsDTO, ProviderPgpDTO } from '@mynexus/mynexus-ui-lib';
import { SelectItem } from 'primeng/api';
import { SubjectOptions } from '@app/products/pac/models/additional-info-fax-subject.enum';
import { ReferralFacility, ServiceFacility } from './../../../constants/document.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toTenDigitFaxNumber } from '@shared/utility';

enum ProviderTypes {
  service = 1,
  order = 2
}

@Component({
    selector: 'app-additional-fax-provider-details',
    templateUrl: './additional-fax-provider-details.component.html',
    styleUrls: ['./additional-fax-provider-details.component.scss'],
    standalone: false
})
export class AdditionalFaxProviderDetailsComponent implements OnInit, OnChanges {
  @Input() servicingFacilityData!: ProviderAccountPgpsDTO & ProviderAccountPgpDTO;

  @Input() orderingProviderData!: PhysicianAccountPgpsDTO;

  @Input() serviceProviderLabel = ServiceFacility;

  @Input() orderingProviderLabel = ReferralFacility;

  @Input() locationFaxConfirmed = true;

  @Input() isPdp: boolean;
  @Input() isOther: boolean;
  @Input() subject: string;

  @Output() outboundModel = new EventEmitter<any>();

  @Output() isValid = new EventEmitter<boolean>();
  @Output() getProviderType = new EventEmitter<any>();
  @Output() subjectSelected = new EventEmitter<string>();

  selectedSubject: string;

  subjectOptions: SelectItem[] = [
    { value: SubjectOptions.REQUEST_FOR_ADDITIONAL_INFORMATION, label: SubjectOptions.REQUEST_FOR_ADDITIONAL_INFORMATION },
    { value: SubjectOptions.POTENTIAL_ADVERSE_DETERMINATION, label: SubjectOptions.POTENTIAL_ADVERSE_DETERMINATION },
    { value: SubjectOptions.OTHER, label: SubjectOptions.OTHER }
  ]

  providerTypes = ProviderTypes;
  faxForm!: FormGroup;

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.initForm();
    this.formChanges();
    this.providerTypeChange();
    if (this.subject !== '') {
      this.faxForm.get('subjectOption')?.setValue(this.subjectOptions[0].value);
      this.faxForm.get('subject')?.setValue(this.subjectOptions[0].value);
      this.subjectSelected.emit(this.subjectOptions[0].value);
    }
    else {
      this.selectedSubject = this.subject;
      this.faxForm.get('subject')?.setValue(this.subject);
      this.subjectSelected.emit(this.subject);
    }

    this.recipientName?.setValue(this.servicingFacilityData?.providerAccountDTO?.providerName);
    this.setProviderAddress();

    // The PAC & HH does Addition Information Fax does not pass the input parameter 'subject', hence added the undefined check
    if (this.subject !== '' && this.subject !== undefined) {
      this.faxForm.get('subject')?.setValue(this.subject);
      this.faxForm.get('subjectOption')?.setValue('');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['servicingFacilityData'] && changes['servicingFacilityData'].firstChange) {
      Promise.resolve().then(() => {
        this.setProviderAddress();
      });
    }
  }

  subjectSelection(event: any) {
    if (!event || !event.value) {
      this.faxForm.get('subjectOption')?.setValue(this.subjectOptions[0].value);
      this.selectedSubject = this.subjectOptions[0].value;
    } else {
      this.selectedSubject = event.value;
    }

    if (this.selectedSubject === SubjectOptions.POTENTIAL_ADVERSE_DETERMINATION) {
      if (this.servicingFacilityData) {
        const { providerAccountDTO } = this.servicingFacilityData;
        this.recipientName?.setValue(providerAccountDTO.providerName);
      }
    }

    if (this.selectedSubject !== SubjectOptions.OTHER) {
      this.isOther = false;
      this.faxForm.get('subject')?.setValue(this.selectedSubject);
    } else {
      this.isOther = true;
      this.faxForm.get('subject')?.setValue('');
    }
    this.subjectSelected.emit(this.selectedSubject);
  }

  get recipientName() {
    return this.faxForm.get('recipientName');
  }

  get providerLocation() {
    return this.faxForm.get('providerLocation');
  }

  get recipientFaxNumber() {
    return this.faxForm.get('recipientFaxNumber');
  }

  initForm() {
    this.faxForm = new FormGroup({
      providerType: new FormControl(ProviderTypes.service, Validators.required),
      recipientName: new FormControl('', [Validators.required, noWhiteSpaceValidator, Validators.maxLength(250)]),
      subjectOption: this.subject ? new FormControl('') : new FormControl('', [Validators.required, noWhiteSpaceValidator, Validators.maxLength(100)]),
      subject: new FormControl('', [Validators.required, noWhiteSpaceValidator, Validators.maxLength(100)]),
      providerLocation: new FormControl('', [Validators.required, noWhiteSpaceValidator, Validators.maxLength(100)]),
      recipientFaxNumber: new FormControl('', [Validators.required, Validators.minLength(10)]),
    });
  }

  providerTypeChange(): void {
    this.getProviderType.emit(this.faxForm.get('providerType').value);
    this.faxForm.get('providerType')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(type => {
      if (type === ProviderTypes.service) {
        this.setProviderAddress();
      }
      this.getProviderType.emit(type);
      if (type === ProviderTypes.order) {
        if (this.orderingProviderData) {
          this.providerLocation?.setValue(this.orderingProviderData.physicianAccountDTO.accountName || '');
          this.recipientFaxNumber?.setValue('');
        } else {
          this.providerLocation?.setValue('');
          this.recipientFaxNumber?.setValue('');
        }
      }
    });
  }

  formChanges(): void {
    this.faxForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.faxForm.valid) {
        this.isValid.emit(true);
        this.outboundModel.emit(this.faxForm.value);
      } else {
        this.isValid.emit(false);
      }
    });
  }

  reset() {
    this.faxForm.reset({ providerType: ProviderTypes.service });
    this.setProviderAddress();
  }

  private setProviderAddress() {
    if (this.servicingFacilityData?.providerAccountDTO) {
      const { providerAccountDTO } = this.servicingFacilityData;
      const providerPgpDTO = this.servicingFacilityData.providerPgpDTOList?.[0] || this.servicingFacilityData.providerPgpDTO || {} as ProviderPgpDTO;
      const providerLocation = providerAccountDTO.providerName || '';

      let recipientFaxNumber = '';

      if (providerPgpDTO) {
        if (providerAccountDTO.locationFaxConfirmed) {
          recipientFaxNumber = providerPgpDTO.authorizationFaxConfirmed ? providerPgpDTO.authorizationFax : providerAccountDTO.locationFax;
        } else {
          recipientFaxNumber = providerPgpDTO.authorizationFax;
        }
      }
      if (this.locationFaxConfirmed) {
        this.providerLocation?.setValue(providerLocation);
      }
      this.recipientFaxNumber?.setValue(toTenDigitFaxNumber(recipientFaxNumber));

    } else {
      this.providerLocation?.setValue('');
      this.recipientFaxNumber?.setValue('');
    }
  }
}
